<script setup lang="ts">
import { onMounted, ref } from 'vue';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import { ButtonType, CustomButton } from '../common';
import SpData from '@/assets/SpData.svg';
import { useReCaptcha } from 'vue-recaptcha-v3';
import { useRoute, useRouter } from 'vue-router';
import {
  LocalStorageUtils,
  routeConstants,
  validateEmail,
  validatePhone
} from '@/utils';
import { BACKEND_URL } from '@/services/apiClient';
import { useAuthStore } from '@/stores/auth';
import { useLoginStore } from '@/stores/login';

const authStore = useAuthStore();
authStore.checkAuth();
const loginStore = useLoginStore();
const reCaptcha = useReCaptcha();
const router = useRouter();
const route = useRoute();
const showAccountCreatedView = ref(false);
const nameError = ref(false);
const emailError = ref(false);
const phoneError = ref(false);


onMounted(() => {
  if (Object.keys(route.query).length > 0) {
    loginStore.resolveBranchLink();
  }
});

const onSignUpClick = async () => {
  loginStore.signUpData.phone = loginStore.signUpData.phone?.replace(/\s+/g, '');

  if (!reCaptcha) {
    const errorObject: Error = {
      name: 'NotInitialzed',
      message: 'ReCaptcha is not initialized'
    };
    loginStore.signUpLoader.setError(errorObject);
    return;
  }

  const errors = validateForm();
  if (errors) {
    return;
  }

  const { executeRecaptcha } = reCaptcha;
  const token = await executeRecaptcha('USER_ACTION');

  loginStore.signUpData['g-recaptcha-response'] = token;
  const data = await loginStore.signUp();
  if (data.setPasswordLink) {
    window.open(`${data.setPasswordLink}`, '_self');
  }
  showAccountCreatedView.value = true;
};

const validateForm = () => {
  nameError.value = !loginStore.signUpData.names;
  emailError.value =
    !loginStore.signUpData.email || !validateEmail(loginStore.signUpData.email);
  phoneError.value =
    !loginStore.signUpData.phone || !validatePhone(loginStore.signUpData.phone);
  return nameError.value || emailError.value || phoneError.value;
};

const backToLogin = () => {
  router.replace({
    name: routeConstants.LOGIN.name
  });
};

const logoutUser = () => {
  LocalStorageUtils.logoutUser();
  authStore.checkAuth();
  loginStore.resolveBranchLink();
};
</script>

<template>
  <div class="signup-root">
    <div class="signup-paper">
      <div class="paper-root"
        v-if="
          !loginStore.signUpLoader.isLoading &&
          !showAccountCreatedView &&
          !authStore.userLoggedIn
        ">
        <div class="logo-root">
          <img alt="Studypages Data" :src="SpData" class="logo" />
        </div>

        <div class="link-data" v-if="loginStore.deepLinkData">
          <img alt="Studypages Data"
            :src="`${BACKEND_URL}${loginStore.deepLinkData.avatar}`"
            class="avatar"/>

          <h1>
            Welcome to
            <span class="header">{{ loginStore.deepLinkData.studyTitle }}</span>
            on Studypages data
          </h1>

          <h3>Group: {{ loginStore.deepLinkData.groupName }}</h3>
        </div>

        <div class="header-root">
          <h1 class="header">Create a new account</h1>
        </div>

        <div class="form">
          <InputText class="input"
            placeholder="Full name"
            v-model="loginStore.signUpData!.names"
            type="text"/>

          <div class="error" v-if="nameError">Invalid name</div>

          <InputText class="input"
            placeholder="Email address"
            v-model="loginStore.signUpData!.email"
            :disabled="loginStore.deepLinkData?.email != undefined"
            type="text"/>

          <div class="error" v-if="emailError">Invalid email</div>

          <vue-tel-input class="input custom-tel-field" v-model="loginStore.signUpData.phone"/>
          <div class="error" v-if="phoneError">Invalid phone</div>
        </div>

        <CustomButton text="Sign Up"
          :type="ButtonType.POSITIVE"
          :fontWeight="600"
          @onClick="onSignUpClick"
          class="button sign-up-button"/>

        <hr />

        <CustomButton text="Have an account? Login!"
          :type="ButtonType.OUTLINED"
          :fontWeight="600"
          @onClick="backToLogin"
          class="button"/>
      </div>

      <div class="paper-loader" v-if="loginStore.signUpLoader.isLoading">
        <ProgressSpinner strokeWidth="4" />
      </div>

      <div class="paper-root" v-if="showAccountCreatedView">
        <div class="logo-root">
          <img alt="Studypages Data" :src="SpData" class="logo" />
        </div>

        <div class="header-root">
          <h1 class="header">New account created!</h1>
        </div>
        <p class="confirmation">
          Your studypages account has been created. You are being redirected to
          set your password.
        </p>
      </div>

      <div class="paper-root" v-if="authStore.userLoggedIn">
        <div class="logo-root">
          <img alt="Studypages Data" :src="SpData" class="logo" />
        </div>

        <p class="confirmation">
          User is already logged in with email:
          {{ LocalStorageUtils.getUserEmail() }}
        </p>

        <CustomButton text="Logout"
          :type="ButtonType.POSITIVE"
          :fontWeight="600"
          @onClick="logoutUser"
          class="button sign-up-button"/>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.signup-root {
  display: flex;
  justify-content: center;
  padding: 80px;
  height: 100vh;
  width: 100%;
  overflow: auto;
}
.signup-paper {
  width: 688px;
  min-height: 600px;
  max-height: calc(100vh - 160px);
  margin: auto 0;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 0 8px 2px rgba(0, 0, 0, 0.15);
  overflow: auto;
}
.paper-root {
  display: flex;
  flex-direction: column;
  padding: 40px 100px;
}
.logo-root,
.header-root {
  display: flex;
  justify-content: center;
  .logo {
    max-width: 220px;
    padding-bottom: 32px;
  }
}
.header {
  font-weight: 700;
  margin-top: 16px;
  margin-bottom: 16px;
}
.input {
  width: 100%;
  margin: 16px 0;
}
.button {
  font-size: 14px;
}
.sign-up-button {
  margin-top: 16px;
}
hr {
  margin: 40px auto;
  width: 100px;
  border-color: rgba(0, 0, 0, 0.12);
}
.paper-loader {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
}
.confirmation {
  text-align: center;
}
.error {
  color: red;
}
.link-data {
  text-align: center;

  h1 {
    font-size: 20px;
  }
}
.avatar {
  width: 80px;
  margin: 20px 0 12px 0;
  border: 1px solid #d9dee4;
  border-radius: 4px;
}
.custom-tel-field {
  border: 1px solid var(--p-inputtext-border-color);
  border-radius: var(--p-inputtext-border-radius);
  height: 39px;
  transition: border-color var(--p-inputtext-transition-duration);
  &:focus-within {
    border-color: var(--p-inputtext-focus-border-color) !important;
    box-shadow: unset;
  }
  &:hover {
    border-color: var(--p-inputtext-focus-border-color) !important;
  }
}
::v-deep(.vti__input) {
  border-radius: var(--p-inputtext-border-radius);
  font-size: 1rem;
  color: var(--p-inputtext-color);
  &::placeholder {
    color: #64748B;
  }
}
::v-deep(.vti__dropdown) {
  border-radius: var(--p-inputtext-border-radius);
}
</style>
